/**
 * OnboardingFlow — the 4-step "create your account" wizard (`/onboarding`).
 *
 * Welcome → Email(+password) → Email OTP(6-digit) → Done, driven by an internal
 * step state machine (one route, no nested routes). Email-first per the unified
 * auth model; the phone-signup backend stays dormant. Backend wiring:
 *   - Email  → `emailSignup({ email, password, language })` (creates the
 *              account + sends the OTP; returns user_id + non-prod dev_otp)
 *   - OTP    → `emailVerify({ user_id, otp })` → setAccessToken → Done
 *
 * The access token lands in `auth-store` (JS memory only — REQ-PRD-§11.2-A);
 * the refresh cookie is set server-side by the verify endpoint.
 *
 * THE 2026-08 REBUILD
 *
 * This is the first thing an anxious, non-expert user ever sees, so the shell
 * now answers the three questions a first-run flow has to answer at all times:
 *
 *   WHERE AM I — a drawn progress band states "Step 2 of 4" in the instrument
 *   face alongside the step's NAME. The five-dot indicator alone said nothing;
 *   `StepDots` stays (it carries the progressbar ARIA the tests assert) but it
 *   is no longer the only signal.
 *   CAN I GO BACK — the back control is present on every step that has a
 *   previous one, and is labelled with the step it returns to (via i18n, not
 *   the hardcoded English `aria-label="Back"` that shipped before).
 *   CAN I LEAVE — Welcome carries the skip; the Email step carries the
 *   "I already have an account" door. Neither is a dead end.
 *
 * The language and theme toggles keep their place in the header: for a user
 * whose first language is Kyrgyz, being able to switch BEFORE reading the first
 * screen is not a preference, it is access. Their aria-labels are now
 * translated too.
 *
 * The project-capture "Basics" step was removed 2026-07-06 (the captured
 * renovation "project" feature is hidden product-wide); onboarding lands the
 * user on the assistant (`/chat`).
 */
import { useState, type ReactElement } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GMotif, Icon, type IconName } from "@garden/ui";

import { Fade } from "@/components/motion";
import { pressClass } from "@/components/motion/Pressable";
import { safeReturnToOrNull, withReturnTo } from "@/features/auth/destination";
import { selectIsAuthenticated, useAuthStore } from "@/lib/auth-store";
import { useUiStore } from "@/lib/ui-store";
import type { SupportedLanguage } from "@/lib/i18n";

import { OnbDone } from "@/features/onboarding/steps/OnbDone";
import { OnbEmail } from "@/features/onboarding/steps/OnbEmail";
import { OnbEmailOtp } from "@/features/onboarding/steps/OnbEmailOtp";
import { OnbWelcome } from "@/features/onboarding/steps/OnbWelcome";
import {
  ONBOARDING_NS,
  registerOnboardingI18n,
  useEmailSignup,
} from "@/features/onboarding/api";
import {
  ONBOARDING_STEPS,
  type OnboardingStep,
} from "@/features/onboarding/steps/shared";

// ─────────────────────────── top toggles (compact) ───────────────────────────

const LANGUAGE_CYCLE: readonly SupportedLanguage[] = ["ru", "ky", "en"] as const;

/** Short codes for the language chip. `ky` prints as KG, matching every other switcher. */
const LANG_CODE: Record<SupportedLanguage, string> = {
  ru: "RU",
  ky: "KG",
  en: "EN",
};

/**
 * Language + theme, available before the user reads a single word. Language
 * cycles RU→KY→EN (the full picker lives in Settings); theme flips light/dark.
 * Both write through the ui-store — the authoritative client preference path.
 * The control shows its CURRENT value ("RU"), not an abstract globe.
 */
function TopToggles(): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const isDark = theme === "dark";
  const nextLanguage =
    LANGUAGE_CYCLE[(LANGUAGE_CYCLE.indexOf(language) + 1) % LANGUAGE_CYCLE.length] ??
    "ru";
  const themeIcon: IconName = isDark ? "sun" : "moon";

  const chipBase =
    "inline-flex h-11 items-center justify-center gap-1.5 rounded-[var(--g-radius)] " +
    "border border-[var(--g-rule)] bg-[var(--g-surface)] text-[13px] font-bold text-foreground " +
    "transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-ground)] " +
    pressClass("cta");

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="onb-language-toggle"
        aria-label={t("a11y.language", {
          current: LANG_CODE[language],
          next: LANG_CODE[nextLanguage],
        })}
        className={`${chipBase} px-3`}
        onClick={() => setLanguage(nextLanguage)}
      >
        <Icon name="globe" size={15} />
        <span className="g-data uppercase">{LANG_CODE[language]}</span>
      </button>
      <button
        type="button"
        data-testid="onb-theme-toggle"
        aria-label={isDark ? t("a11y.themeLight") : t("a11y.themeDark")}
        aria-pressed={isDark}
        className={`${chipBase} w-11`}
        onClick={() => setTheme(isDark ? "light" : "dark")}
      >
        <Icon name={themeIcon} size={18} />
      </button>
    </div>
  );
}

// ─────────────────────────── shell ───────────────────────────

interface OnbShellProps {
  step: OnboardingStep;
  onBack: () => void;
  /** Hide the back control (first step, and the completed step). */
  hideBack?: boolean;
  children: ReactElement;
}

/**
 * Onboarding chrome: a back control + the toggles, then a DRAWN progress band
 * naming the step, over the `--g-ground` canvas. The content column is centred
 * and width-capped (480px) so a line of Kyrgyz — 15–25% longer than the Russian
 * — still breaks in the same shape.
 */
function OnbShell({
  step,
  onBack,
  hideBack = false,
  children,
}: OnbShellProps): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  const current = ONBOARDING_STEPS.indexOf(step) + 1;
  const total = ONBOARDING_STEPS.length;

  return (
    <div
      data-testid="onboarding-flow"
      data-step={step}
      className="flex min-h-dvh flex-col bg-[var(--g-ground)]"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
        <button
          type="button"
          data-testid="onb-back"
          aria-label={t("a11y.back")}
          onClick={onBack}
          className={[
            "inline-flex h-11 items-center gap-1.5 rounded-[var(--g-radius)] border border-[var(--g-rule)]",
            "bg-[var(--g-surface)] px-3 text-[13px] font-bold text-foreground transition-colors",
            "hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-ground)]",
            hideBack ? "invisible" : "",
            pressClass("cta"),
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Icon name="chevL" size={17} />
          <span>{t("cta.back")}</span>
        </button>
        <TopToggles />
      </header>

      {/*
        The progress band — the armature of the flow, drawn rather than implied.
        It states the position AND the name of the step, then draws the same
        thing as a segmented rule.

        The segmented rule REPLACES `@garden/ui`'s `StepDots` here (it keeps the
        `step-dots` test id and the identical progressbar ARIA). Two reasons:
        a row of dots is the generic wizard trope this rebuild is refusing, and
        `StepDots` exposes `role="progressbar"` with no way to pass an
        accessible name — axe flags every onboarding screen for
        `aria-progressbar-name`, and the name has to live on the element that
        carries the role.
      */}
      <div className="border-y border-[var(--g-rule)] bg-[var(--g-sunken)]">
        <div className="mx-auto flex w-full max-w-[480px] items-center justify-between gap-4 px-4 py-2.5">
          <p className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--g-ink-2)]">
            <span className="g-data">{`${current}/${total}`}</span>
            <span aria-hidden="true"> · </span>
            {t(`steps.${step}`)}
          </p>
          <div
            role="progressbar"
            aria-label={t("a11y.progress")}
            aria-valuenow={current}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuetext={t("a11y.progressValue", { current, total })}
            data-testid="step-dots"
            className="flex shrink-0 items-center gap-1"
          >
            {Array.from({ length: total }, (_, i) => i + 1).map((index) => (
              <span
                key={index}
                aria-hidden="true"
                className={[
                  "block h-[3px] w-6 rounded-full transition-colors duration-200",
                  index <= current ? "bg-[var(--g-primary)]" : "bg-[var(--g-rule)]",
                ].join(" ")}
              />
            ))}
          </div>
        </div>
      </div>

      <main className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6">
        {/* Re-key on step so each screen's entrance replays on transition. */}
        <Fade key={step} className="flex flex-1 flex-col">
          {children}
        </Fade>
      </main>
    </div>
  );
}

// ─────────────────────────── flow ───────────────────────────

export interface OnboardingFlowProps {
  /** Initial step — defaults to `welcome`. Exposed for tests/deep-links. */
  initialStep?: OnboardingStep;
}

/**
 * The onboarding state machine. Holds the credentials captured on the Email
 * step (needed across Email → OTP → Done) and orchestrates the transitions plus
 * the two backend calls. Each step is presentational and calls back up here.
 */
export function OnboardingFlow({
  initialStep = "welcome",
}: OnboardingFlowProps): ReactElement {
  registerOnboardingI18n();
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const language = useUiStore((s) => s.language);
  // W0.1: a guest who started sign-up from the chat goes back to it.
  const [searchParams] = useSearchParams();
  const rawReturnTo = searchParams.get("return_to");
  const afterAuth = safeReturnToOrNull(rawReturnTo) ?? "/chat";

  const [step, setStep] = useState<OnboardingStep>(initialStep);
  // Credentials captured on the Email step. `password` is kept so the OTP
  // step's "Resend" can re-issue; `userId` is the verify payload.
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  // NON-PROD demo affordance: the signup response echoes the plaintext email
  // OTP so the OTP step shows it on-screen (no real email in QA). Null in prod.
  const [devOtp, setDevOtp] = useState<string | null>(null);

  // "Resend" re-issues the OTP via /auth/signup (the backend re-issues for an
  // existing-but-unverified email) and refreshes the echoed dev_otp.
  const resendSignup = useEmailSignup();

  // A user who lands on the wizard ALREADY signed in (still at "welcome") is
  // sent to the assistant. Gating on the welcome step is what makes this safe:
  // the in-wizard login at the OTP step flips auth but the step is no longer
  // "welcome", so the guard does NOT eject the user mid-flow.
  if (isAuthenticated && step === "welcome") {
    return <Navigate to={afterAuth} replace />;
  }

  const goBack = (): void => {
    switch (step) {
      case "welcome":
        navigate("/");
        return;
      case "email":
        setStep("welcome");
        return;
      case "emailOtp":
        setStep("email");
        return;
      case "done":
        // Verification already happened — there is nothing to go back TO, so
        // the control is hidden on this step and this is a defensive fallback.
        navigate(afterAuth);
        return;
    }
  };

  const content = ((): ReactElement => {
    switch (step) {
      case "welcome":
        return (
          <OnbWelcome
            onContinue={() => setStep("email")}
            onSkip={() => navigate(afterAuth)}
            renderMark={() => (
              /* The shyrdak motif with real presence — it IS the mark, not a
                 3%-opacity texture hiding behind an icon. Sized DOWN on short
                 phones: at 168px the Kyrgyz copy (15–25% longer than the
                 Russian) pushed the "skip" control below the fold on a 390×844
                 screen, and a first-run flow may not hide its way out. */
              <div className="relative mb-6 grid h-[132px] w-[132px] place-items-center overflow-hidden rounded-[var(--g-radius-lg)] bg-primary text-[var(--g-on-primary)] sm:h-[168px] sm:w-[168px]">
                <GMotif
                  tone="ink"
                  opacity={0.42}
                  className="absolute inset-0 scale-110 text-[var(--g-on-primary)]"
                />
                <Icon name="sparkles" size={52} className="relative" />
              </div>
            )}
          />
        );
      case "email":
        return (
          <OnbEmail
            onSignIn={() => navigate(withReturnTo("/login", rawReturnTo))}
            onSent={({ email: e, password: p, userId: uid, devOtp: code }) => {
              setEmail(e);
              setPassword(p);
              setUserId(uid);
              setDevOtp(code);
              setStep("emailOtp");
            }}
          />
        );
      case "emailOtp":
        return (
          <OnbEmailOtp
            email={email}
            userId={userId}
            devOtp={devOtp}
            onChangeEmail={() => setStep("email")}
            onResend={() => {
              resendSignup.mutate(
                { email, password, language },
                { onSuccess: (data) => setDevOtp(data.dev_otp ?? null) },
              );
            }}
            onVerified={(accessToken) => {
              setAccessToken(accessToken);
              setStep("done");
            }}
          />
        );
      case "done":
        return <OnbDone onOpenAssistant={() => navigate(afterAuth)} />;
    }
  })();

  return (
    <OnbShell
      step={step}
      onBack={goBack}
      hideBack={step === "welcome" || step === "done"}
    >
      {content}
    </OnbShell>
  );
}

export default OnboardingFlow;
